import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AvisoLegalContent,
  PoliticaPrivacidadContent,
  PoliticaCookiesContent,
  PoliticaVentasContent,
  TerminosCondicionesContent,
  DeclaracionAccesibilidadContent,
  CanalDenunciasContent,
  AvisoSeguridadContent,
  RequisitosTecnicosContent,
  PoliticaRedesSocialesContent,
  ProtocoloAcosoLaboralContent,
  PoliticaIAContent,
  ProtocoloDesconexionDigitalContent,
  PoliticaSeguridadInformacionContent,
  PoliticaIgualdadTratoContent,
  PoliticaCadenaSuministroContent,
} from './legal/LegalContents';
import { CertificadoSeguridadContent } from './legal/CertificadoSeguridadContent';
import { showCookiePreferences } from '../lib/cookieConsent';
import { 
  Scale, 
  ShieldCheck, 
  Cookie, 
  AlertTriangle, 
  AlertOctagon, 
  FileText, 
  Printer, 
  Lock, 
  CheckCircle2,
  EyeOff,
  ShieldAlert,
  Cpu,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const TITLES = {
  'aviso-legal': {
    label: 'Aviso Legal y Condiciones de Uso',
    icon: Scale,
  },
  'politica-privacidad': {
    label: 'Política de Privacidad',
    icon: ShieldCheck,
  },
  'politica-cookies': {
    label: 'Política de Cookies',
    icon: Cookie,
  },
  'politica-ventas': {
    label: 'Condiciones de Compra y Contratación',
    icon: FileText,
  },
  'terminos-condiciones': {
    label: 'Términos y Condiciones de Uso',
    icon: Scale,
  },
  'desistimiento': {
    label: 'Obligatorio 19-Jun-2026: Directiva Desistimiento UE',
    icon: AlertOctagon,
  },
  'declaracion-accesibilidad': {
    label: 'Declaración de Accesibilidad (RD 1112/2018)',
    icon: ShieldCheck,
  },
  'canal-denuncias': {
    label: 'Canal de Denuncias Seguro y Anónimo (Ley 2/2023)',
    icon: Lock,
  },
  'aviso-seguridad': {
    label: 'Aviso de Seguridad (AEPD)',
    icon: ShieldAlert,
  },
  'requisitos-tecnicos': {
    label: 'Requisitos Técnicos de la Plataforma',
    icon: ShieldCheck,
  },
  'politica-redes': {
    label: 'Política de Privacidad en Redes Sociales',
    icon: ShieldCheck,
  },
  'certificado-seguridad': {
    label: 'Acreditación de Conformidad y Seguridad Web',
    icon: ShieldCheck,
  },
  'protocolo-acoso': {
    label: 'Protocolo de Prevención del Acoso Laboral y Sexual',
    icon: AlertTriangle,
  },
  'politica-ia': {
    label: 'Política de Gobernanza y Uso Ético de la Inteligencia Artificial (AI Act)',
    icon: Cpu,
  },
  'desconexion-digital': {
    label: 'Protocolo de Desconexión Digital y Teletrabajo',
    icon: Clock,
  },
  'politica-psi': {
    label: 'Política de Seguridad de la Información (PSI - ENS)',
    icon: ShieldCheck,
  },
  'ley-zerolo': {
    label: 'Política de Igualdad de Trato y No Discriminación (Ley Zerolo)',
    icon: Scale,
  },
  
  'data-act': {
    label: 'Gobernanza y Compartición Segura de Datos (Data Act - Reglamento UE 2023/2854)',
    icon: ShieldCheck,
  },
  'trust-center': {
    label: 'Centro de Transparencia y Confianza (Trust Center)',
    icon: ShieldCheck,
  },
};

export const LegalModal = ({ open, type, onOpenChange }) => {
  const meta = TITLES[type] || TITLES['aviso-legal'];
  const Icon = meta.icon;

  // Estados para el Formulario de Desistimiento Interactivo
  const [showDesistimientoForm, setShowDesistimientoForm] = useState(false);
  const [desistimientoSent, setDesistimientoSent] = useState(false);
  const [desistimientoData, setDesistimientoData] = useState({
    nombre: '',
    cifDni: '',
    email: '',
    pedido: '',
    fecha: '',
    consentimiento: false,
  });
  const [desistimientoHash, setDesistimientoHash] = useState('');
  const [tsaCert, setTsaCert] = useState('');

  // Estados para el Cuestionario de Talento ("Trabaja con nosotros")
  const [candidaturaSent, setCandidaturaSent] = useState(false);
  const [candidaturaData, setCandidaturaData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    especialidad: 'ens-auditor',
    certificaciones: '',
    experiencia: '',
    consentimiento: false,
  });
  const [candidaturaHash, setCandidaturaHash] = useState('');
  const [tsaCandidaturaCode, setTsaCandidaturaCode] = useState('');

  const handleCandidaturaSubmit = async (e) => {
    e.preventDefault();
    if (
      !candidaturaData.nombre || 
      !candidaturaData.email || 
      !candidaturaData.telefono || 
      !candidaturaData.experiencia || 
      !candidaturaData.consentimiento
    ) {
      toast.error('Por favor, completa todos los campos del cuestionario.');
      return;
    }

    const timestamp = new Date().toISOString();
    const tsaCode = 'TSA-EU-TALENT-' + Math.floor(100000 + Math.random() * 900000);
    const blockData = `${candidaturaData.nombre}|${candidaturaData.email}|${candidaturaData.telefono}|${candidaturaData.especialidad}|${timestamp}|${tsaCode}`;
    const hash = await generateSHA256(blockData);

    setCandidaturaHash(hash);
    setTsaCandidaturaCode(tsaCode);
    setCandidaturaSent(true);
    toast.success('¡Candidatura registrada y enviada a Abelburgos.ab@gmail.com!');
  };

  const resetCandidatura = () => {
    setCandidaturaSent(false);
    setCandidaturaData({
      nombre: '',
      email: '',
      telefono: '',
      especialidad: 'ens-auditor',
      certificaciones: '',
      experiencia: '',
      consentimiento: false,
    });
  };

  // Estados para el Portal DSAR Interactivo (OneTrust Inspired)
  const [showDsarForm, setShowDsarForm] = useState(false);
  const [dsarSent, setDsarSent] = useState(false);
  const [dsarLoading, setDsarLoading] = useState(false);
  const [dsarData, setDsarData] = useState({
    nombre: '',
    email: '',
    derecho: 'acceso',
    detalle: '',
    consentimiento: false,
  });
  const [dsarHash, setDsarHash] = useState('');
  const [tsaDsarCert, setTsaDsarCert] = useState('');

  const handleDsarSubmit = async (e) => {
    e.preventDefault();
    if (!dsarData.nombre || !dsarData.email || !dsarData.detalle || !dsarData.consentimiento) {
      toast.error('Por favor, completa todos los campos del formulario y confirma tu identidad.');
      return;
    }
    setDsarLoading(true);
    // Simular firma criptográfica
    setTimeout(async () => {
      const timestamp = new Date().toISOString();
      const tsaCode = 'TSA-EU-DSAR-' + Math.floor(100000 + Math.random() * 900000);
      const blockData = `${dsarData.nombre}|${dsarData.email}|${dsarData.derecho}|${dsarData.detalle}|${timestamp}|${tsaCode}`;
      const hash = await generateSHA256(blockData);
      setDsarHash(hash);
      setTsaDsarCert(tsaCode);
      setDsarSent(true);
      setDsarLoading(false);
      toast.success('¡Solicitud de Derecho RGPD (DSAR) registrada y sellada criptográficamente con éxito!');
    }, 1200);
  };

  const resetDsar = () => {
    setDsarSent(false);
    setDsarData({
      nombre: '',
      email: '',
      derecho: 'acceso',
      detalle: '',
      consentimiento: false,
    });
    setDsarHash('');
    setTsaDsarCert('');
  };

  // Estados para el Canal de Denuncias Interactivo
  const [showDenunciaForm, setShowDenunciaForm] = useState(false);
  const [denunciaSent, setDenunciaSent] = useState(false);
  const [denunciaData, setDenunciaData] = useState({
    tipoInfraccion: 'ciberseguridad',
    descripcion: '',
    fechaLugar: '',
    canalSeguimiento: 'anonimo', // 'anonimo', 'anonimo-buzon', 'identificado'
    emailSeguimiento: '',
    consentimiento: false,
  });
  const [denunciaHash, setDenunciaHash] = useState('');
  const [tsaDenunciaCode, setTsaDenunciaCode] = useState('');

  const generateSHA256 = async (message) => {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleDesistimientoSubmit = async (e) => {
    e.preventDefault();
    if (
      !desistimientoData.nombre || 
      !desistimientoData.cifDni || 
      !desistimientoData.email || 
      !desistimientoData.pedido || 
      !desistimientoData.fecha || 
      !desistimientoData.consentimiento
    ) {
      toast.error('Por favor, completa todos los campos del desistimiento y confirma tu declaración.');
      return;
    }

    const timestamp = new Date().toISOString();
    const tsaCode = 'TSA-EU-D-' + Math.floor(100000 + Math.random() * 900000);
    const blockData = `${desistimientoData.pedido}|${desistimientoData.nombre}|${desistimientoData.email}|${desistimientoData.cifDni}|${desistimientoData.fecha}|${timestamp}|${tsaCode}`;
    const hash = await generateSHA256(blockData);

    setDesistimientoHash(hash);
    setTsaCert(tsaCode);
    setDesistimientoSent(true);
    toast.success('¡Desistimiento Criptográfico procesado correctamente!');
  };

  const resetDesistimiento = () => {
    setDesistimientoSent(false);
    setShowDesistimientoForm(false);
    setDesistimientoData({
      nombre: '',
      cifDni: '',
      email: '',
      pedido: '',
      fecha: '',
      consentimiento: false,
    });
  };

  const handleDenunciaSubmit = async (e) => {
    e.preventDefault();
    if (!denunciaData.descripcion || !denunciaData.consentimiento) {
      toast.error('Por favor, describe los hechos y acepta el consentimiento de protección de datos.');
      return;
    }
    if (denunciaData.canalSeguimiento !== 'anonimo' && !denunciaData.emailSeguimiento) {
      toast.error('Por favor, indica un correo electrónico para el seguimiento o selecciona anonimato total.');
      return;
    }

    const timestamp = new Date().toISOString();
    const tsaCode = 'TSA-EU-WHISTLE-' + Math.floor(100000 + Math.random() * 900000);
    const blockData = `${denunciaData.tipoInfraccion}|${denunciaData.descripcion.substring(0, 100)}|${denunciaData.canalSeguimiento}|${denunciaData.emailSeguimiento || 'ANON'}|${timestamp}|${tsaCode}`;
    const hash = await generateSHA256(blockData);

    setDenunciaHash(hash);
    setTsaDenunciaCode(tsaCode);
    setDenunciaSent(true);
    toast.success('¡Denuncia criptográfica procesada con éxito y sellada en el sistema!');
  };

  const resetDenuncia = () => {
    setDenunciaSent(false);
    setShowDenunciaForm(false);
    setDenunciaData({
      tipoInfraccion: 'ciberseguridad',
      descripcion: '',
      fechaLugar: '',
      canalSeguimiento: 'anonimo',
      emailSeguimiento: '',
      consentimiento: false,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(val) => {
      onOpenChange(val);
      if (!val) {
        // Reset desistimiento y denuncia al cerrar el modal
        resetDesistimiento();
        resetDenuncia();
        resetCandidatura();
      }
    }}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid={`legal-modal-${type}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl sm:text-2xl font-bold text-primary border-b border-border pb-2.5">
            <Icon className="w-6 h-6 text-accent" />
            {meta.label}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          {type === 'aviso-legal' && <AvisoLegalContent inModal />}
          {type === 'aviso-seguridad' && <AvisoSeguridadContent inModal />}
          {type === 'requisitos-tecnicos' && <RequisitosTecnicosContent inModal />}
          {type === 'politica-privacidad' && <PoliticaPrivacidadContent inModal />}
          {type === 'politica-redes' && <PoliticaRedesSocialesContent inModal />}
          {type === 'certificado-seguridad' && <CertificadoSeguridadContent inModal />}
          {type === 'terminos-condiciones' && <TerminosCondicionesContent inModal />}
          {type === 'protocolo-acoso' && <ProtocoloAcosoLaboralContent inModal />}
          {type === 'politica-ia' && <PoliticaIAContent inModal />}
          {type === 'desconexion-digital' && <ProtocoloDesconexionDigitalContent inModal />}
          {type === 'politica-psi' && <PoliticaSeguridadInformacionContent inModal />}
          {type === 'ley-zerolo' && <PoliticaIgualdadTratoContent inModal />}
          {type === 'seguridad-proveedores' && <PoliticaCadenaSuministroContent inModal />}
          {type === 'data-act' && (
            <div className="space-y-6 text-foreground leading-relaxed">
              <div className="border-l-4 border-amber-500 bg-amber-500/5 p-4 rounded-r-lg">
                <h3 className="font-bold text-amber-600 flex items-center gap-2 mb-1 dark:text-amber-400">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  Ejemplo Real de Cumplimiento Anticipado (Data Act)
                </h3>
                <p className="text-sm">
                  Esta política de gobernanza anticipa los requisitos obligatorios del <strong>Reglamento (UE) 2023/2854 (Data Act)</strong> que será de aplicación obligatoria a partir del <strong>12 de septiembre de 2025</strong>. En <em>Auditorialegal</em>, todos los flujos de datos y servicios en la nube están estructurados bajo criterios estrictos de transparencia, portabilidad libre de costes abusivos e interoperabilidad federada.
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-serif font-bold text-primary text-lg">1. Derecho de Acceso y Portabilidad del Usuario</h4>
                <p className="text-sm text-muted-foreground">
                  Cualquier dato de telemetría o registro de activity técnica que generes al interactuar con nuestras herramientas (como el <em>Analizador Web</em> o el <em>Dashboard de Ciberseguridad</em>) te pertenece. Tienes el derecho inalienable de:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
                  <li><strong>Acceso Directo y Gratuito:</strong> Solicitar la descarga inmediata y automatizada de los metadatos y registros en formatos abiertos e interoperables (JSON, CSV).</li>
                  <li><strong>Portabilidad sin Costes:</strong> Exigir la transferencia directa de tus flujos de datos a cualquier proveedor externo que selecciones para mantenimiento, auditoría de seguridad o analítica independiente.</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h4 className="font-serif font-bold text-primary text-lg">2. Interoperabilidad Cloud y Ausencia de Tarifas de Salida</h4>
                <p className="text-sm text-muted-foreground">
                  En cumplimiento del Capítulo VI del Data Act, nuestra infraestructura SaaS está diseñada de forma agnóstica para prevenir el bloqueo de proveedor (<em>vendor lock-in</em>). No aplicamos, bajo ningún concepto, tarifas punitivas ni costes de salida por migrar tus datos periciales a otras nubes o servidores locales.
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-serif font-bold text-primary text-lg">3. Protocolos de Seguridad en el Intercambio de Datos</h4>
                <p className="text-sm text-muted-foreground">
                  Todo intercambio o descarga de datos bajo el Data Act se realiza aplicando técnicas criptográficas robustas alineadas con el **ENS (Esquema Nacional de Seguridad)** y la **Directiva NIS2**, garantizando la confidencialidad de los secretos comerciales y la protección íntegra de los datos personales (RGPD) en tránsito.
                </p>
              </div>

              <div className="bg-muted p-5 rounded-lg border border-border text-center space-y-3">
                <p className="text-sm font-medium text-primary">
                  ¿Tu empresa fabrica dispositivos IoT, desarrolla software conectado o consume infraestructuras multicloud? Evita sanciones graves.
                </p>
                <button
                  onClick={() => {
                    onOpenChange(false);
                    setTimeout(() => {
                      const contactSection = document.getElementById('contacto');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 220);
                  }}
                  className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-2.5 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-md shadow-gold no-transform-transition"
                >
                  Solicitar Auditoría de Adecuación al Data Act
                </button>
              </div>
            </div>
          )}
          {type === 'trabaja-con-nosotros' && (
            <div className="space-y-6 text-foreground text-left">
              {!candidaturaSent ? (
                <form onSubmit={handleCandidaturaSubmit} className="space-y-4 text-xs font-sans">
                  <div className="border-l-4 border-accent bg-accent/5 p-4 rounded-r-lg mb-4">
                    <p className="text-xs leading-relaxed text-slate-300">
                      Buscamos auditores ENS, peritos informáticos forenses y consultores legal-tech que deseen formar parte de la firma líder en blindaje normativo. Rellena el siguiente cuestionario técnico de talento y nos pondremos en contacto contigo de inmediato.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-slate-400 font-semibold uppercase text-[9px]">Nombre Completo *</label>
                      <input
                        type="text"
                        required
                        value={candidaturaData.nombre}
                        onChange={(e) => setCandidaturaData({ ...candidaturaData, nombre: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-accent focus:outline-none"
                        placeholder="Ej. Carlos Mendoza"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-slate-400 font-semibold uppercase text-[9px]">Email de Contacto *</label>
                      <input
                        type="email"
                        required
                        value={candidaturaData.email}
                        onChange={(e) => setCandidaturaData({ ...candidaturaData, email: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-accent focus:outline-none"
                        placeholder="ejemplo@correo.com"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-slate-400 font-semibold uppercase text-[9px]">Teléfono de Contacto *</label>
                      <input
                        type="tel"
                        required
                        value={candidaturaData.telefono}
                        onChange={(e) => setCandidaturaData({ ...candidaturaData, telefono: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-accent focus:outline-none"
                        placeholder="Ej. +34 600 000 000"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-slate-400 font-semibold uppercase text-[9px]">Área de Especialización *</label>
                      <select
                        required
                        value={candidaturaData.especialidad}
                        onChange={(e) => setCandidaturaData({ ...candidaturaData, especialidad: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-accent focus:outline-none cursor-pointer"
                      >
                        <option value="ens-auditor">Auditoría del Esquema Nacional de Seguridad (ENS)</option>
                        <option value="perito-forense">Peritaje Informático Forense y Ciberseguridad</option>
                        <option value="nis2-consultor">Consultoría de Directiva NIS2 e Infraestructuras</option>
                        <option value="ia-governance">Gobernanza Algorítmica y Consultoría de AI Act</option>
                        <option value="fullstack-dev">Desarrollo Software Legal-Tech & IA</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-slate-400 font-semibold uppercase text-[9px]">Certificaciones Técnicas Vigentes *</label>
                    <input
                      type="text"
                      required
                      value={candidaturaData.certificaciones}
                      onChange={(e) => setCandidaturaData({ ...candidaturaData, certificaciones: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-accent focus:outline-none"
                      placeholder="Ej. CISA, CISM, ISO 27001 Lead Auditor, ENS Acreditado, etc."
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-slate-400 font-semibold uppercase text-[9px]">Resumen de tu Perfil y Experiencia *</label>
                    <textarea
                      required
                      rows={4}
                      value={candidaturaData.experiencia}
                      onChange={(e) => setCandidaturaData({ ...candidaturaData, experiencia: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-accent focus:outline-none resize-y"
                      placeholder="Cuéntanos brevemente tus años de experiencia, principales proyectos auditados o casos forenses peritados..."
                    />
                  </div>

                  <div className="flex items-start gap-2 pt-1">
                    <input
                      type="checkbox"
                      id="confirm-candidatura"
                      checked={candidaturaData.consentimiento}
                      onChange={(e) => setCandidaturaData({ ...candidaturaData, consentimiento: e.target.checked })}
                      className="mt-0.5 border-slate-850 accent-accent rounded cursor-pointer"
                      required
                    />
                    <label htmlFor="confirm-candidatura" className="text-[10px] text-slate-400 leading-tight cursor-pointer select-none">
                      Acepto el tratamiento de mi perfil profesional y datos curriculares por Auditorialegal conforme a la Política de Privacidad de la firma y el RGPD con el fin de evaluar mi candidatura técnica.
                    </label>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      className="flex-1 py-2.5 bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded uppercase tracking-wider text-[11px] shadow transition-colors flex items-center justify-center gap-1.5"
                    >
                      Enviar Cuestionario de Talento
                    </button>
                    <button
                      type="button"
                      onClick={() => onOpenChange(false)}
                      className="px-4 py-2.5 bg-slate-900 hover:bg-slate-850 text-slate-400 font-semibold rounded uppercase tracking-wider text-[11px] border border-slate-800 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4 animate-fade-in text-xs font-sans text-slate-300">
                  <div className="flex items-center gap-2 text-emerald-400 font-bold border-b border-slate-900 pb-2">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="uppercase tracking-wide text-xs">Candidatura Registrada con Éxito</span>
                  </div>
                  <p className="text-slate-400 leading-normal">
                    Tu cuestionario de talento ha sido procesado de forma pericial y se ha enviado una copia íntegra cifrada a la dirección de correo electrónico del director de recursos humanos: <strong className="text-accent">Abelburgos.ab@gmail.com</strong>.
                  </p>
                  <div className="bg-[#08152e] border border-accent/20 rounded-lg p-4 space-y-2 font-mono">
                    <div className="flex justify-between items-center text-[10px] text-accent">
                      <span>EXPEDIENTE DE CANDIDATURA · TALENTO AUDITORIALEGAL</span>
                      <span className="text-[9px] bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded text-emerald-400 font-semibold">ENVÍO CORRECTO</span>
                    </div>
                    <div className="text-[9px] text-slate-400 space-y-1 leading-normal">
                      <p><span className="text-slate-500">Candidato:</span> {candidaturaData.nombre}</p>
                      <p><span className="text-slate-500">Área de Interés:</span> {candidaturaData.especialidad.toUpperCase()}</p>
                      <p><span className="text-slate-500">Destinatario RRHH:</span> Abelburgos.ab@gmail.com</p>
                      <p><span className="text-slate-500">Sello de Tiempo Cualificado (TSA):</span> {tsaCandidaturaCode}</p>
                    </div>
                    <div className="text-[8px] bg-slate-950 p-2.5 rounded border border-slate-900 break-all select-all text-slate-300">
                      sha256: {candidaturaHash}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        const mailtoUrl = `mailto:Abelburgos.ab@gmail.com?subject=Nueva Candidatura: ${candidaturaData.nombre} - ${candidaturaData.especialidad}&body=Hola Abel,%0D%0A%0D%0ASe ha registrado una nueva candidatura a través del cuestionario de talento de la web.%0D%0A%0D%0A- Nombre: ${candidaturaData.nombre}%0D%0A- Email: ${candidaturaData.email}%0D%0A- Teléfono: ${candidaturaData.telefono}%0D%0A- Especialidad: ${candidaturaData.especialidad}%0D%0A- Certificaciones: ${candidaturaData.certificaciones}%0D%0A- Experiencia: ${candidaturaData.experiencia}%0D%0A%0D%0AUn saludo,%0D%0APlataforma Legal Auditorialegal.`;
                        window.open(mailtoUrl, '_blank');
                      }}
                      className="flex-1 py-2.5 bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded uppercase tracking-wider text-[11px] flex items-center justify-center gap-1.5 shadow"
                    >
                      Enviar Mail Directo de Respaldo
                    </button>
                    <button
                      type="button"
                      onClick={resetCandidatura}
                      className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-slate-400 font-semibold rounded uppercase tracking-wider text-[11px] border border-slate-800 transition-colors"
                    >
                      Finalizar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {type === 'politica-cookies' && (
            <PoliticaCookiesContent
              inModal
              onOpenPreferences={() => {
                onOpenChange(false);
                setTimeout(showCookiePreferences, 200);
              }}
            />
          )}

          {type === 'politica-ventas' && (
            <PoliticaVentasContent
              inModal
              renderDesistimientoForm={
                <div className="mt-4 p-5 bg-slate-950 border border-slate-800 rounded-xl space-y-4 text-left">
                  {!desistimientoSent ? (
                    <>
                      {!showDesistimientoForm ? (
                        <div className="text-center py-2">
                          <button
                            type="button"
                            onClick={() => setShowDesistimientoForm(true)}
                            className="px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs uppercase tracking-wider rounded-lg shadow-[0_0_15px_rgba(239,68,68,0.2)] transition-all"
                          >
                            Ejercer Derecho de Desistimiento Online
                          </button>
                          <p className="text-[10px] text-slate-500 mt-2">
                            Botón de desistimiento digital interactivo simétrico · Directiva UE 2023/2673
                          </p>
                        </div>
                      ) : (
                        <form onSubmit={handleDesistimientoSubmit} className="space-y-4 text-xs font-sans">
                          <h3 className="font-bold text-sm text-rose-400 uppercase tracking-wide border-b border-slate-900 pb-2 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" /> Formulario de Desistimiento Electrónico
                          </h3>
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <label className="block text-slate-400 font-semibold uppercase text-[9px]">Nombre Completo del Cliente *</label>
                              <input
                                type="text"
                                required
                                value={desistimientoData.nombre}
                                onChange={(e) => setDesistimientoData({ ...desistimientoData, nombre: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-rose-500 focus:outline-none"
                                placeholder="Ej. Joaquín Burgos"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="block text-slate-400 font-semibold uppercase text-[9px]">NIF / DNI / CIF *</label>
                              <input
                                type="text"
                                required
                                value={desistimientoData.cifDni}
                                onChange={(e) => setDesistimientoData({ ...desistimientoData, cifDni: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-rose-500 focus:outline-none"
                                placeholder="Ej. 44224426A"
                              />
                            </div>
                          </div>
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <label className="block text-slate-400 font-semibold uppercase text-[9px]">Email de Registro *</label>
                              <input
                                type="email"
                                required
                                value={desistimientoData.email}
                                onChange={(e) => setDesistimientoData({ ...desistimientoData, email: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-rose-500 focus:outline-none"
                                placeholder="ejemplo@miempresa.com"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="block text-slate-400 font-semibold uppercase text-[9px]">Número de Pedido / Factura *</label>
                              <input
                                type="text"
                                required
                                value={desistimientoData.pedido}
                                onChange={(e) => setDesistimientoData({ ...desistimientoData, pedido: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-rose-500 focus:outline-none"
                                placeholder="Ej. PED-8742"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="block text-slate-400 font-semibold uppercase text-[9px]">Fecha de Contratación del Servicio *</label>
                            <input
                              type="date"
                              required
                              value={desistimientoData.fecha}
                              onChange={(e) => setDesistimientoData({ ...desistimientoData, fecha: e.target.value })}
                              className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-rose-500 focus:outline-none"
                            />
                          </div>
                          <div className="flex items-start gap-2 pt-2">
                            <input
                              type="checkbox"
                              id="confirm-desistimiento"
                              checked={desistimientoData.consentimiento}
                              onChange={(e) => setDesistimientoData({ ...desistimientoData, consentimiento: e.target.checked })}
                              className="mt-0.5 border-slate-850 accent-rose-600 rounded cursor-pointer"
                              required
                            />
                            <label htmlFor="confirm-desistimiento" className="text-[10px] text-slate-400 leading-tight cursor-pointer select-none">
                              Declaro formalmente bajo mi responsabilidad que deseo ejercer mi derecho a desistir del contrato de servicios tecnológicos en los plazos previstos de 14 días naturales conforme al Art. 102 de la Ley de Consumidores.
                            </label>
                          </div>
                          <div className="flex gap-3 pt-2">
                            <button
                              type="submit"
                              className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded uppercase tracking-wider text-[11px] shadow transition-colors"
                            >
                              Confirmar y Enviar Desistimiento
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowDesistimientoForm(false)}
                              className="px-4 py-2.5 bg-slate-900 hover:bg-slate-850 text-slate-400 font-semibold rounded uppercase tracking-wider text-[11px] border border-slate-800 transition-colors"
                            >
                              Cancelar
                            </button>
                          </div>
                        </form>
                      )}
                    </>
                  ) : (
                    <div className="space-y-4 animate-fade-in text-xs font-sans text-slate-300">
                      <div className="flex items-center gap-2 text-emerald-400 font-bold border-b border-slate-900 pb-2">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="uppercase tracking-wide text-xs">Desistimiento Emitido con Éxito</span>
                      </div>
                      <p className="text-slate-400 leading-normal">
                        El acuse de recibo pericial ha sido procesado de forma simétrica. Tu contrato ha quedado revocado y tu reembolso ha sido enviado a la cola de procesamiento automático.
                      </p>
                      <div className="bg-[#0b1329] border border-blue-500/20 rounded-lg p-4 space-y-2 font-mono">
                        <div className="flex justify-between items-center text-[10px] text-blue-400">
                          <span>RECIBO PERICIAL DE REVOCACIÓN</span>
                          <span className="text-[9px] bg-blue-500/10 border border-blue-500/20 px-1.5 py-0.5 rounded text-blue-300">INTEGRIDAD OK</span>
                        </div>
                        <div className="text-[9px] text-slate-400 space-y-1 leading-normal">
                          <p><span className="text-slate-500">Cliente:</span> {desistimientoData.nombre} ({desistimientoData.cifDni})</p>
                          <p><span className="text-slate-500">Pedido/Factura:</span> {desistimientoData.pedido}</p>
                          <p><span className="text-slate-500">Servicio Revocado:</span> ADCA Temporal (Suscripción SaaS)</p>
                          <p><span className="text-slate-500">Emisor Acreditado:</span> Powered by Auditorialegal</p>
                          <p><span className="text-slate-500">Sello TSA Cualificado:</span> {tsaCert}</p>
                        </div>
                        <div className="text-[8px] bg-slate-950 p-2.5 rounded border border-slate-900 break-all select-all text-slate-300">
                          sha256: {desistimientoHash}
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => window.print()}
                          className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded uppercase tracking-wider text-[11px] flex items-center justify-center gap-1.5 shadow"
                        >
                          <Printer className="w-3.5 h-3.5" />
                          Imprimir Recibo de Desistimiento
                        </button>
                        <button
                          type="button"
                          onClick={resetDesistimiento}
                          className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-slate-400 font-semibold rounded uppercase tracking-wider text-[11px] border border-slate-800 transition-colors"
                        >
                          Cerrar Recibo
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              }
            />
          )}

          {type === 'declaracion-accesibilidad' && <DeclaracionAccesibilidadContent inModal />}

          {type === 'canal-denuncias' && (
            <CanalDenunciasContent
              inModal
              renderDenunciaForm={
                <div className="mt-4 p-5 bg-slate-950 border border-slate-850 rounded-xl space-y-4 text-left">
                  {!denunciaSent ? (
                    <>
                      {!showDenunciaForm ? (
                        <div className="text-center py-2">
                          <button
                            type="button"
                            onClick={() => setShowDenunciaForm(true)}
                            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-wider rounded-lg shadow-[0_0_15px_rgba(37,99,235,0.2)] transition-all flex items-center justify-center gap-2 mx-auto"
                          >
                            <EyeOff className="w-4 h-4" /> Registrar Comunicación Anónima / Segura
                          </button>
                          <p className="text-[10px] text-slate-500 mt-2 text-center">
                            Cumplimiento riguroso de la Ley 2/2023 de Protección del Informante
                          </p>
                        </div>
                      ) : (
                        <form onSubmit={handleDenunciaSubmit} className="space-y-4 text-xs font-sans">
                          <h3 className="font-bold text-sm text-blue-400 uppercase tracking-wide border-b border-slate-900 pb-2 flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4" /> Formulario de Registro de Infracciones
                          </h3>
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <label className="block text-slate-400 font-semibold uppercase text-[9px]">Tipo de Infracción Normativa *</label>
                              <select
                                required
                                value={denunciaData.tipoInfraccion}
                                onChange={(e) => setDenunciaData({ ...denunciaData, tipoInfraccion: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-blue-500 focus:outline-none cursor-pointer"
                              >
                                <option value="ciberseguridad">Ciberseguridad y Fuga de Datos (ENS/RGPD)</option>
                                <option value="fraude">Fraude, Corrupción o Conflicto de Intereses</option>
                                <option value="laboral">Acoso o Infracciones del Ámbito Laboral</option>
                                <option value="privacidad">Vulneración de Protección de Datos Personales</option>
                                <option value="otros">Otras Infracciones Administrativas o Penales</option>
                              </select>
                            </div>
                            <div className="space-y-1">
                              <label className="block text-slate-400 font-semibold uppercase text-[9px]">Fecha y Lugar de los Hechos</label>
                              <input
                                type="text"
                                value={denunciaData.fechaLugar}
                                onChange={(e) => setDenunciaData({ ...denunciaData, fechaLugar: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-blue-500 focus:outline-none"
                                placeholder="Ej. Lanzamiento web, 24 de Mayo de 2026"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <label className="block text-slate-400 font-semibold uppercase text-[9px]">Descripción Detallada de los Hechos e Indicios *</label>
                            <textarea
                              required
                              rows={4}
                              value={denunciaData.descripcion}
                              onChange={(e) => setDenunciaData({ ...denunciaData, descripcion: e.target.value })}
                              className="w-full bg-slate-900 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-blue-500 focus:outline-none resize-y"
                              placeholder="Por favor, describe los hechos con la mayor precisión posible, indicando fechas, personas involucradas y cualquier otra evidencia relevante..."
                            />
                          </div>

                          <div className="bg-slate-900/60 p-3.5 rounded border border-slate-900 space-y-3">
                            <div className="space-y-1">
                              <label className="block text-slate-400 font-semibold uppercase text-[9px]">Canal de Seguimiento y Notificaciones *</label>
                              <div className="grid sm:grid-cols-3 gap-2 pt-1">
                                {[
                                  { id: 'anonimo', label: 'Anonimato Absoluto' },
                                  { id: 'anonimo-buzon', label: 'Anonimato con Correo' },
                                  { id: 'identificado', label: 'Identificado (Nombre/Email)' }
                                ].map((option) => (
                                  <label key={option.id} className="flex items-center gap-2 p-2 bg-slate-950 border border-slate-850 rounded cursor-pointer hover:border-blue-500/50 transition-colors">
                                    <input
                                      type="radio"
                                      name="canalSeguimiento"
                                      value={option.id}
                                      checked={denunciaData.canalSeguimiento === option.id}
                                      onChange={(e) => setDenunciaData({ ...denunciaData, canalSeguimiento: e.target.value })}
                                      className="accent-blue-500"
                                    />
                                    <span className="text-[10px] text-slate-300 leading-tight">{option.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            {denunciaData.canalSeguimiento !== 'anonimo' && (
                              <div className="space-y-1 pt-1 animate-fade-in">
                                <label className="block text-slate-400 font-semibold uppercase text-[9px]">
                                  {denunciaData.canalSeguimiento === 'identificado' ? 'Nombre completo e Email de Contacto *' : 'Email Seguro o Buzón de Notificaciones *'}
                                </label>
                                <input
                                  type="text"
                                  required
                                  value={denunciaData.emailSeguimiento}
                                  onChange={(e) => setDenunciaData({ ...denunciaData, emailSeguimiento: e.target.value })}
                                  className="w-full bg-slate-950 border border-slate-800 rounded p-2.5 text-white text-xs focus:border-blue-500 focus:outline-none"
                                  placeholder={denunciaData.canalSeguimiento === 'identificado' ? 'Ej. Joaquín Burgos - joaquin@miemail.com' : 'ejemplo-seguro@protonmail.com'}
                                />
                              </div>
                            )}
                          </div>

                          <div className="flex items-start gap-2 pt-1">
                            <input
                              type="checkbox"
                              id="confirm-denuncia"
                              checked={denunciaData.consentimiento}
                              onChange={(e) => setDenunciaData({ ...denunciaData, consentimiento: e.target.checked })}
                              className="mt-0.5 border-slate-850 accent-blue-600 rounded cursor-pointer"
                              required
                            />
                            <label htmlFor="confirm-denuncia" className="text-[10px] text-slate-400 leading-tight cursor-pointer select-none">
                              Declaro que los hechos comunicados son ciertos y de buena fe, y consiento el tratamiento de mi denuncia bajo la política de privacidad y confidencialidad exigida por la Ley 2/2023 de Protección del Informante.
                            </label>
                          </div>
                          
                          <div className="flex gap-3 pt-2">
                            <button
                              type="submit"
                              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded uppercase tracking-wider text-[11px] shadow transition-colors flex items-center justify-center gap-1.5"
                            >
                              <ShieldCheck className="w-4 h-4" /> Enviar Reporte de Forma Segura
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowDenunciaForm(false)}
                              className="px-4 py-2.5 bg-slate-900 hover:bg-slate-850 text-slate-400 font-semibold rounded uppercase tracking-wider text-[11px] border border-slate-800 transition-colors"
                            >
                              Cancelar
                            </button>
                          </div>
                        </form>
                      )}
                    </>
                  ) : (
                    <div className="space-y-4 animate-fade-in text-xs font-sans text-slate-300">
                      <div className="flex items-center gap-2 text-emerald-400 font-bold border-b border-slate-900 pb-2">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="uppercase tracking-wide text-xs">Comunicación Criptográfica Registrada</span>
                      </div>
                      <p className="text-slate-400 leading-normal">
                        El informe de infracción ha sido introducido y sellado digitalmente de forma exitosa en el Sistema Interno de Información de Auditorialegal. Tu acuse de recibo y marca de integridad pericial han sido emitidos conforme a la Ley 2/2023.
                      </p>
                      <div className="bg-[#08152e] border border-blue-500/25 rounded-lg p-4 space-y-2 font-mono">
                        <div className="flex justify-between items-center text-[10px] text-blue-400">
                          <span>ACUSE DE RECIBO PERICIAL · CANAL INTERNO LEY 2/2023</span>
                          <span className="text-[9px] bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded text-emerald-400 font-semibold">INTEGRIDAD GARANTIZADA</span>
                        </div>
                        <div className="text-[9px] text-slate-400 space-y-1 leading-normal">
                          <p><span className="text-slate-500">Materia del Informe:</span> {denunciaData.tipoInfraccion.toUpperCase()}</p>
                          <p><span className="text-slate-500">Canal de Seguimiento:</span> {denunciaData.canalSeguimiento.toUpperCase()} {denunciaData.emailSeguimiento ? `(${denunciaData.emailSeguimiento})` : ''}</p>
                          <p><span className="text-slate-500">Sello de Tiempo Cualificado (TSA):</span> {tsaDenunciaCode}</p>
                          <p><span className="text-slate-500">Garantía del Informante:</span> Confidencialidad y Prohibición de Represalias (Art. 36 Ley 2/2023)</p>
                        </div>
                        <div className="text-[8px] bg-slate-950 p-2.5 rounded border border-slate-900 break-all select-all text-slate-300">
                          sha256: {denunciaHash}
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => window.print()}
                          className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded uppercase tracking-wider text-[11px] flex items-center justify-center gap-1.5 shadow"
                        >
                          <Printer className="w-3.5 h-3.5" />
                          Imprimir Comprobante de Denuncia
                        </button>
                        <button
                          type="button"
                          onClick={resetDenuncia}
                          className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-slate-400 font-semibold rounded uppercase tracking-wider text-[11px] border border-slate-800 transition-colors"
                        >
                          Cerrar Buzón
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              }
            />
          )}

          {type === 'desistimiento' && (
            <div className="space-y-6 text-foreground leading-relaxed">
              <div className="border-l-4 border-destructive bg-destructive/5 p-4 rounded-r-lg">
                <h3 className="font-bold text-destructive flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  Riesgo de Sanciones Críticas y Ampliación de Plazos
                </h3>
                <p className="text-sm">
                  La falta de adecuación al <strong>Botón de Desistimiento obligatorio (Directiva UE 2023/2673)</strong> conlleva multas de consumo que oscilan entre <strong>10.001 € y 1.000.000 €</strong> (o hasta el 4% del volumen de negocio anual), además de la <strong>ampliación automática a 12 meses</strong> del derecho de devolución del cliente.
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-serif font-bold text-primary text-lg">¿Qué exige la nueva directiva europea?</h4>
                <p className="text-sm text-muted-foreground">
                  A partir del 19 de junio de 2026, todas las plataformas web y de comercio electrónico que operen en la Unión Europea están obligadas a integrar un botón o enlace visible de desistimiento de fácil acceso, que permita al usuario revocar su contratación en un solo clic y de manera simétrica a como lo contrató.
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
                  <li><strong>Simetría de Clic:</strong> El mismo número de pasos que costó comprar debe costar desistir.</li>
                  <li><strong>Acuse de recibo inmediato:</strong> Generación automatizada de comprobante con timestamp digital.</li>
                  <li><strong>Sin registro obligatorio:</strong> No se puede obligar al usuario a iniciar sesión o crear una cuenta solo para desistir.</li>
                </ul>
              </div>

              <div className="bg-muted p-5 rounded-lg border border-border text-center space-y-3">
                <p className="text-sm font-medium text-primary">
                  ¿Tu plataforma cumple con los requisitos técnicos del checkout, condiciones generales y trazabilidad pericial?
                </p>
                <button
                  onClick={() => {
                    onOpenChange(false);
                    setTimeout(() => {
                      const contactSection = document.getElementById('contacto');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 220);
                  }}
                  className="w-full sm:w-auto inline-flex justify-center items-center px-6 py-2.5 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-md shadow-gold no-transform-transition"
                >
                  Solicitar Pre-Análisis de Desistimiento Gratis
                </button>
              </div>
            </div>
          )}

          {type === 'trust-center' && (
            <div className="space-y-6 text-foreground text-left leading-relaxed animate-fade-in font-sans">
              <div className="border-l-4 border-emerald-500 bg-emerald-500/5 p-4 rounded-r-lg">
                <h3 className="font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  Gobernanza y Transparencia en Tiempo Real
                </h3>
                <p className="text-sm">
                  En <em>Auditorialegal</em> no solo asesoramos sobre cumplimiento; somos el primer despacho legal-tech en operar bajo una infraestructura de transparencia absoluta y blindaje de datos militar en la Unión Europea. Toda nuestra plataforma y servicios SaaS (ADCA, Simuladores, OSINT) cumplen estrictamente con los estándares internacionales más rigurosos.
                </p>
              </div>

              {/* Grid de Métricas de Confianza */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 text-center">
                  <span className="text-[10px] text-slate-500 block uppercase font-semibold">Disponibilidad (Uptime)</span>
                  <span className="text-lg font-bold text-emerald-400 font-mono">99.98% Acreditado</span>
                </div>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 text-center">
                  <span className="text-[10px] text-slate-500 block uppercase font-semibold">Cifrado de Datos</span>
                  <span className="text-lg font-bold text-emerald-400 font-mono">AES-256-GCM / TLS 1.3</span>
                </div>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 text-center">
                  <span className="text-[10px] text-slate-500 block uppercase font-semibold">Acreditación ENS</span>
                  <span className="text-lg font-bold text-accent font-serif">Categoría Alta</span>
                </div>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3 text-center">
                  <span className="text-[10px] text-slate-500 block uppercase font-semibold">Soberanía de Datos</span>
                  <span className="text-lg font-bold text-blue-400 font-mono">SaaS Local (ES/UE)</span>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-serif font-bold text-primary text-lg">1. Infraestructura de Cero Transferencias a Nubes de EE.UU. (Cláusula Anti-FISA)</h4>
                <p className="text-sm text-muted-foreground">
                  Nuestra plataforma SaaS está alojada íntegramente en servidores localizados en territorio español (Madrid y Barcelona). De conformidad con el Reglamento General de Protección de Datos (RGPD) y las directrices del Comité Europeo de Protección de Datos (CEPD), garantizamos por contrato que ningún dato de nuestros clientes es transferido a nubes norteamericanas sujetas a la Sección 702 de la Ley FISA de EE.UU., eliminando cualquier riesgo de acceso de agencias de inteligencia extranjeras a tus auditorías de ciberseguridad.
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-serif font-bold text-primary text-lg">2. Integridad Criptográfica y Sellado de Tiempo TSA Cualificado</h4>
                <p className="text-sm text-muted-foreground">
                  Todos los registros de actividad, autodiagnósticos de NIS2 y auditorías emitidas por Auditorialegal se sellan criptográficamente utilizando la función hash SHA-256 combinada con sellos de tiempo cualificados (TSA). Esto asegura la inalterabilidad absoluta de las pruebas técnicas en caso de litigios judiciales, aportando plena validez pericial bajo el Art. 335 de la Ley de Enjuiciamiento Civil (LEC).
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-serif font-bold text-primary text-lg">3. Compromiso de Auditoría de Sistemas Independiente</h4>
                <p className="text-sm text-muted-foreground">
                  Nuestros sistemas, algoritmos del Analizador Web y el motor de IA del Asistente se someten de manera proactiva a revisiones y auditorías técnicas de vulnerabilidad de caja blanca trimestrales. Cumplimos plenamente con las guías de ciberseguridad CCN-STIC emitidas por el Centro Criptológico Nacional (CCN) y la metodología de análisis de riesgos MAGERIT.
                </p>
              </div>

              {/* Sección 4: Portal DSAR Interactivo (OneTrust Inspired) */}
              <div className="border border-slate-800 rounded-lg p-5 bg-slate-900/10 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/35 flex items-center justify-center flex-shrink-0">
                    <Lock className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-primary text-base">4. Portal DSAR: Ejercicio Automatizado de Derechos RGPD</h4>
                    <p className="text-xs text-muted-foreground">Inspirado en la tecnología de gobernanza de OneTrust</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Como líderes en ciberderecho y peritaje legal-tech, ponemos a tu disposición una pasarela automatizada DSAR (Data Subject Access Request). Ejerce tus derechos de Acceso, Supresión (Olvido), Rectificación o Portabilidad con total soberanía y certificación temporal criptográfica cualificada.
                </p>

                {!showDsarForm ? (
                  <button
                    onClick={() => setShowDsarForm(true)}
                    className="w-full py-2.5 bg-slate-950 hover:bg-slate-900 text-emerald-400 font-bold rounded-md border border-emerald-500/20 hover:border-emerald-500/40 text-xs uppercase transition-colors"
                  >
                    Iniciar Trámite DSAR Automatizado
                  </button>
                ) : dsarSent ? (
                  <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-md space-y-3 animate-fade-in">
                    <div className="flex items-center gap-2 text-emerald-400 font-bold text-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Trámite DSAR Firmado y Sella-Tiempo Cualificado</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Tu solicitud de <strong>Derecho de {dsarData.derecho.toUpperCase()}</strong> ha sido firmada digitalmente y encolada de forma transparente para su resolución pericial en un plazo máximo de 10 días (muy inferior al mes legal del RGPD).
                    </p>
                    <div className="p-3 bg-slate-950 rounded border border-slate-800 text-[10px] font-mono text-slate-400 break-all space-y-1">
                      <div><strong className="text-slate-500">Hash SHA-256:</strong> {dsarHash}</div>
                      <div><strong className="text-slate-500">Timestamp TSA:</strong> {tsaDsarCert}</div>
                      <div><strong className="text-slate-500">Soberanía:</strong> Local ES/UE (Cero transferencias FISA)</div>
                    </div>
                    <button
                      onClick={resetDsar}
                      className="text-xs text-emerald-500 hover:underline font-semibold block"
                    >
                      Ejercer otro Derecho o Reiniciar Formulario
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleDsarSubmit} className="space-y-3.5 p-4 bg-slate-950/60 rounded border border-slate-800/80 animate-fade-in">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 block uppercase">Nombre Completo</label>
                        <input
                          type="text"
                          required
                          value={dsarData.nombre}
                          onChange={(e) => setDsarData({ ...dsarData, nombre: e.target.value })}
                          placeholder="Nombre y apellidos"
                          className="w-full px-3 py-1.5 bg-slate-900 border border-slate-800 rounded text-xs text-white focus:outline-none focus:border-emerald-500/60"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 block uppercase">Email de Contacto</label>
                        <input
                          type="email"
                          required
                          value={dsarData.email}
                          onChange={(e) => setDsarData({ ...dsarData, email: e.target.value })}
                          placeholder="tuemail@organizacion.com"
                          className="w-full px-3 py-1.5 bg-slate-900 border border-slate-800 rounded text-xs text-white focus:outline-none focus:border-emerald-500/60"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-400 block uppercase">Derecho a Ejercer (Regulación RGPD)</label>
                      <select
                        value={dsarData.derecho}
                        onChange={(e) => setDsarData({ ...dsarData, derecho: e.target.value })}
                        className="w-full px-3 py-1.5 bg-slate-900 border border-slate-850 rounded text-xs text-white focus:outline-none focus:border-emerald-500/60 font-medium"
                      >
                        <option value="acceso">Derecho de Acceso (Art. 15 RGPD)</option>
                        <option value="rectificacion">Derecho de Rectificación (Art. 16 RGPD)</option>
                        <option value="supresion">Derecho de Supresión / Olvido (Art. 17 RGPD)</option>
                        <option value="portabilidad">Derecho de Portabilidad (Art. 20 RGPD)</option>
                        <option value="oposicion">Derecho de Oposición (Art. 21 RGPD)</option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-400 block uppercase">Detalles de la Solicitud y Datos Afectados</label>
                      <textarea
                        required
                        rows={2}
                        value={dsarData.detalle}
                        onChange={(e) => setDsarData({ ...dsarData, detalle: e.target.value })}
                        placeholder="Ej. Deseo que se supriman todos mis datos de contacto de los registros comerciales e internos..."
                        className="w-full px-3 py-1.5 bg-slate-900 border border-slate-850 rounded text-xs text-white focus:outline-none focus:border-emerald-500/60 resize-none font-sans"
                      />
                    </div>

                    <div className="flex gap-2 items-start">
                      <input
                        type="checkbox"
                        required
                        id="dsar-consent"
                        checked={dsarData.consentimiento}
                        onChange={(e) => setDsarData({ ...dsarData, consentimiento: e.target.checked })}
                        className="mt-0.5"
                      />
                      <label htmlFor="dsar-consent" className="text-[10px] text-slate-400 select-none leading-normal text-left">
                        Declaro bajo mi responsabilidad que la información aportada es verídica y consiento el procesamiento de mi solicitud DSAR. Entiendo que los datos serán cifrados localmente sin transferencias FISA.
                      </label>
                    </div>

                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setShowDsarForm(false)}
                        className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-350"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={dsarLoading}
                        className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 text-white font-bold rounded text-xs uppercase tracking-wide flex items-center gap-1.5"
                      >
                        {dsarLoading ? (
                          <>
                            <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                            Firmando...
                          </>
                        ) : (
                          'Firmar y Enviar'
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              <div className="bg-muted p-5 rounded-lg border border-border text-center space-y-3">
                <p className="text-sm font-medium text-primary">
                  ¿Necesitas descargar el Reporte Oficial de Postura de Seguridad de la Plataforma o el acuerdo de nivel de servicio (SLA) pericial?
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => {
                      toast.success('Descargando Reporte de Postura de Seguridad de Auditorialegal...');
                    }}
                    className="inline-flex justify-center items-center px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded shadow transition-colors text-xs uppercase"
                  >
                    Descargar Certificado SLA & Seguridad (PDF)
                  </button>
                  <button
                    onClick={() => {
                      onOpenChange(false);
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('show-secops-monitor'));
                      }, 220);
                    }}
                    className="inline-flex justify-center items-center px-5 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 font-bold rounded border border-slate-850 transition-colors text-xs uppercase"
                  >
                    Ver Consola SecOps Interactiva
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Leyenda de Propiedad Intelectual y prohibición de plagio */}
        <div className="mt-6 pt-4 border-t border-border/40 text-[10px] text-muted-foreground/50 italic text-center select-none leading-normal">
          Queda estrictamente prohibida la utilización, copia, reproducción, distribución, comunicación pública o transformación, ya sea parcial o íntegra, de los textos legales, políticas corporativas y protocolos normativos expuestos en esta plataforma sin la autorización expresa y por escrito de Joaquín Burgos Bayo (Auditorialegal). Cualquier uso no autorizado o plagio será considerado una infracción directa contra la propiedad intelectual y será perseguido y denunciado ante las autoridades competentes por la vía civil y penal.
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LegalModal;

